import React, { useEffect, useState, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import "../styles/Pages/dashboard.css"
import ClickImpressionBox from "../components/Dashboard/ClicksImpressionBox"
import MostViewedItem from "../components/Dashboard/MostViewedItem"
import AreaChartWithZoom from "../components/Dashboard/Chart"
import Dropdown from "../components/Common/Dropdown"
import { useSelector } from "react-redux"
import { Analytics } from "../Apis/analyticsApi"
import { Drawer, Spin, DatePicker } from "antd"
import StoreDrawer from "../components/Common/StoreDrawer"
import { getUserCookies } from "../utils/Cookies"
import { StatBox, withStats } from "../components/Dashboard/Statbox"
import { jwtDecode } from "jwt-decode"
import SubscriptionChart from "../components/Dashboard/SubscriptionChart"
import BusinessMetricsChart from "../components/Dashboard/BusinessMetricsChart"
import { translations } from "../utils/Translation/staticTranslation"
import { isProfileModuleReadOnly, effectiveHighestRole } from "../utils/modulePermissionSidebar"
import dayjs from "dayjs"
import DropdownWithDatePicker from "../components/Dashboard/DropdownWithDatePicker"
import AnalyticsAdmin from "../components/Dashboard/AnalyticsAdmin"
import FeatureUsageChart from "../components/Dashboard/FeatureUsageChart"
import CardTypeByMethodChart from "../components/Dashboard/CardTypeByMethodChart"
import UserRevenueInsights from "../components/Dashboard/UserRevenueInsights"
import Insightsbycountry from "../components/Dashboard/Insightsbycountry"
import UserManagmentTable from "../components/Dashboard/UserManagmentTable"
import AdminLoginAnalytics from "../components/Dashboard/AdminLoginAnalytics"

const Dashboard = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const highest_role = decoded?.highest_role
  const market_role = decoded?.market_role
  const user = useSelector((state) => state.user.user)
  const effectiveHighest = effectiveHighestRole(user, highest_role)
  const isSuperAdmin =
    effectiveHighest === "SUPERADMIN" ||
    highest_role === "SUPERADMIN" ||
    String(market_role ?? "") === "1" ||
    String(market_role ?? "") === "3"
  const lang = localStorage.getItem("language") || "ar"
  const currentLanguage = translations[lang]?.AnalyticsPages
  const dashboardAdmin = translations[lang]?.DashboardSuperAdmin
  const MerchantsCounts = withStats(StatBox)

  const [selectedDays, setSelectedDays] = useState(7)
  const [loading, setLoading] = useState(false)
  const [metricsData, setMetricsData] = useState([])
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [selectedStore, setSelectedStore] = useState(null)
  const [merchantsStats, setMerchantsStats] = useState({})
  const [selectedDaysLabel, setSelectedDaysLabel] = useState("Last 30 days")
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  })

  const [userSummary, setUserSummary] = useState(null)
  const [userRevenueData, setUserRevenueData] = useState(null)

  const stores = useSelector((state) => state.store.stores)
  const dashboardReadOnly = isProfileModuleReadOnly(user, "dashboard")
  const { RangePicker } = DatePicker

  // ✅ Unified filter state stored in Dashboard
  const [filterState, setFilterState] = useState(() => {
    const currentLang = localStorage.getItem("language") || "ar"
    return {
      startDate: "2025-07-01",
      endDate: dayjs().format("YYYY-MM-DD"),
      filterType: "tilldate",
      label: currentLang === "ar" ? "حتى التاريخ" : "Till Date",
    }
  })

  // ✅ Use refs to track if initial data has been loaded
  const isInitialLoadRef = useRef(true)
  const hasLoadedSuperAdminDataRef = useRef(false)

  // ✅ Function to handle filter changes
  const handleFilterSelect = (filter) => {
    setFilterState(filter)

    if (filter?.country?.length) {
      const uniqueCountries = [...new Set(filter.country)]
    }
  }

  // ✅ Set selected store only once when stores are available
  useEffect(() => {
    if (
      !isSuperAdmin &&
      stores &&
      stores.length > 0 &&
      !selectedStore
    ) {
      setSelectedStore(stores[0])
    }
  }, [stores, isSuperAdmin, selectedStore])

  const fetchGraphData = async (days, startDate = null, endDate = null) => {
    setLoading(true)
    try {
      const formattedStartDate = startDate
        ? dayjs(startDate).format("YYYY-MM-DD")
        : null
      const formattedEndDate = endDate
        ? dayjs(endDate).format("YYYY-MM-DD")
        : null

      const response = await Analytics.getProductMetrics(
        selectedStore?.id,
        days,
        formattedStartDate,
        formattedEndDate
      )
      if (response.status === 200) {
        setMetricsData(response.data)
      }
    } catch (error) {
      console.error("Error:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSuperAdminMetrics = async () => {
    setLoading(true)
    try {
      const response = await Analytics.getSuperAdminMetrics()
      if (response.status === 200) {
        setMerchantsStats(response.data.data)
      }
    } catch (error) {
      console.error("Error:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserPaymentSummary = async (filterOverride = filterState) => {
    const { startDate, endDate, filterType, country } = filterOverride
    setLoading(true)
    try {
      // Convert "tilldate" to "customdate" for backend (like merchant-analytics)
      const typeToSend = filterType === "tilldate" ? "customdate" : filterType

      const response = await Analytics.getUserPaymentSummary(
        startDate,
        endDate,
        typeToSend,
        country || []
      )
      if (response.status === 200) {
        setUserSummary(response.data)
      }
    } catch (error) {
      console.error("Error fetching user payment summary:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserRevenueGraph = async (filterOverride = filterState) => {
    setLoading(true)
    try {
      const filterToUse = filterOverride || filterState
      let typeToSend = filterToUse?.filterType || "last30days"
      let startDateToSend = null
      let endDateToSend = null

      // Convert "tilldate" to "customdate" for backend (like merchant-analytics)
      if (typeToSend === "tilldate") {
        typeToSend = "customdate"
        startDateToSend = filterToUse.startDate
        endDateToSend = filterToUse.endDate
      } else if (typeToSend === "customdate") {
        startDateToSend = filterToUse.startDate
        endDateToSend = filterToUse.endDate
      }

      const response = await Analytics.getUserRevenueGraph(
        startDateToSend,
        endDateToSend,
        typeToSend,
        filterToUse.country || []
      )

      if (response.status === 200) {
        setUserRevenueData(response.data)
      }
    } catch (error) {
      console.error("Error fetching user-revenue graph:", error)
    } finally {
      setLoading(false)
    }
  }

  // ✅ SINGLE useEffect for SUPERADMIN initial load
  useEffect(() => {
    if (isSuperAdmin && !hasLoadedSuperAdminDataRef.current) {
      hasLoadedSuperAdminDataRef.current = true

      const defaultFilter = {
        startDate: "2025-07-01",
        endDate: dayjs().format("YYYY-MM-DD"),
        filterType: "tilldate",
        label: lang === "ar" ? "حتى التاريخ" : "Till Date",
      }

      setFilterState(defaultFilter)

      // Fetch all SUPERADMIN data in parallel
      Promise.all([
        fetchSuperAdminMetrics(),
        fetchUserPaymentSummary(defaultFilter),
        fetchUserRevenueGraph(defaultFilter),
      ])
    }
  }, [isSuperAdmin])

  // ✅ Handle filter changes for SUPERADMIN (only after initial load)
  useEffect(() => {
    if (
      isSuperAdmin &&
      hasLoadedSuperAdminDataRef.current &&
      !isInitialLoadRef.current
    ) {
      Promise.all([fetchUserPaymentSummary(), fetchUserRevenueGraph()])
    }

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false
    }
  }, [filterState, isSuperAdmin])

  // ✅ Handle non-SUPERADMIN data fetching
  useEffect(() => {
    if (
      !isSuperAdmin &&
      selectedStore &&
      selectedDays !== null
    ) {
      fetchGraphData(selectedDays)
    }
  }, [selectedDays, selectedStore, isSuperAdmin])

  // ✅ Handle custom date range for non-SUPERADMIN
  useEffect(() => {
    if (
      !isSuperAdmin &&
      selectedStore &&
      selectedDaysLabel === "Custom Range" &&
      customDateRange.startDate &&
      customDateRange.endDate
    ) {
      fetchGraphData(null, customDateRange.startDate, customDateRange.endDate)
    }
  }, [customDateRange, selectedDaysLabel, isSuperAdmin])

  const handleDropdownChange = (value) => {
    if (value === "Custom Range") {
      setSelectedDays(null)
      setSelectedDaysLabel(value)
      return
    }

    const daysMap = {
      "Last 7 days": 7,
      "Last 30 days": 30,
      "Last 90 days": 90,
    }

    setCustomDateRange({ startDate: null, endDate: null })
    setSelectedDays(daysMap[value])
    setSelectedDaysLabel(value)
  }

  const disabledDate = (current) => {
    return current && current > dayjs().endOf("day")
  }

  const normalizePath = (path) => {
    if (!path) return "/"
    const trimmed = path.replace(/\/+$/, "")
    return trimmed === "" ? "/" : trimmed
  }
  const currentPath = normalizePath(location.pathname)

  return (
    <div>
      <div className="d-flex justify-content-between" style={{ gap: 16 }}>
        <div
          className="d-flex justify-content-between align-items-center w-100"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            height: "100%",
            paddingBottom: "15px",
          }}
        >
          <h1
            className="text-141414 text-20 font-600 m-0"
            style={{
              display: "flex",
              alignItems: "center",
              margin: 0,
            }}
          >
            {currentLanguage?.welcome}{" "}
            {lang === "ar" ? user?.name_arabic : user?.name_english}
          </h1>

          <div
            className="d-flex align-items-center"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              flexWrap: "wrap",
              height: "100%",
            }}
          >
            {isSuperAdmin && (
              <>
                <div className="nav-switch">
                  <button
                    className={`navbar-tab ${
                      currentPath === "/dashboard/merchant-analytics"
                        ? "active"
                        : ""
                    }`}
                    onClick={() => navigate("/dashboard/merchant-analytics")}
                  >
                    {lang === "ar" ? "التاجر" : "Merchant"}
                  </button>
                  <button
                    className={`navbar-tab ${
                      currentPath === "/dashboard" ? "active" : ""
                    }`}
                    onClick={() => navigate("/dashboard")}
                  >
                    {lang === "ar" ? "تقرير الذكاء الاصطناعي" : "AI Dashboard"}
                  </button>
                </div>
                <DropdownWithDatePicker
                  currentLanguage={currentLanguage}
                  onFilterSelect={handleFilterSelect}
                />
              </>
            )}

            {/*{!isSuperAdmin && (*/}
            {/*  <div className="nav-switch">*/}
            {/*    <button*/}
            {/*      className={`navbar-tab ${*/}
            {/*        currentPath === "/dashboard" ? "active" : ""*/}
            {/*      }`}*/}
            {/*      onClick={() => navigate("/dashboard")}*/}
            {/*    >*/}
            {/*      {lang === "ar" ? "لوحة التحكم" : "Dashboard"}*/}
            {/*    </button>*/}
            {/*    /!* Salla Analytics tab temporarily hidden *!/*/}
            {/*    /!* <button*/}
            {/*      className={`navbar-tab ${*/}
            {/*        currentPath === "/dashboard/salla-analytics" ? "active" : ""*/}
            {/*      }`}*/}
            {/*      onClick={() => navigate("/dashboard/salla-analytics")}*/}
            {/*    >*/}
            {/*      {lang === "ar" ? "تحليلات سلة" : "Salla Analytics"}*/}
            {/*    </button> *!/*/}
            {/*  </div>*/}
            {/*)}*/}
          </div>
        </div>

        {!isSuperAdmin && (
          <div
            className="select-store"
            onClick={
              dashboardReadOnly ? undefined : () => setDrawerVisible(true)
            }
            style={
              dashboardReadOnly
                ? { cursor: "not-allowed", opacity: 0.65 }
                : undefined
            }
            title={
              dashboardReadOnly
                ? lang === "ar"
                  ? "عرض فقط"
                  : "View only"
                : undefined
            }
          >
            <p className={`mb-0 ${lang === "ar" ? "ml-10" : "mr-10"}`}>
              {lang === "ar"
                ? selectedStore?.store_name_arabic
                : selectedStore?.store_name ||
                  currentLanguage?.select_store}{" "}
            </p>
            <img
              src="/icons/arrowSelectionDark.svg"
              style={{
                transform: lang === "ar" ? "rotateY(180deg)" : "none",
              }}
              alt=""
            />
          </div>
        )}
      </div>

      {isSuperAdmin ? (
        <AnalyticsAdmin data={userSummary} />
      ) : null}

      {isSuperAdmin ? (
        <div className="charts-row donuts-row">
          <section className="chart-card plan-card">
            <h3 className="panel-title">
              {lang === "ar" ? "توزيع الباقات" : "Plan Distribution"}
            </h3>
            <div className="panel-body">
              <SubscriptionChart data={userSummary} />
            </div>
          </section>

          <section className="chart-card card-method-card">
            <h3 className="panel-title">
              {lang === "ar"
                ? "نوع البطاقة حسب طريقة الدفع"
                : "Card Type by Payment Method"}
            </h3>
            <div className="panel-body card-method-body">
              <CardTypeByMethodChart data={userSummary} />
            </div>
          </section>
        </div>
      ) : null}

      {isSuperAdmin ? (
        <div className="charts-row feature-row">
          <section
            className={`chart-card feature-card ${
              lang === "ar" ? "feature-card-v2" : "feature-card-v2-eng"
            } `}
          >
            <h3 className="panel-title">
              {" "}
              {lang === "ar" ? "الميزات المستخدمة" : "Feature Usage"}
            </h3>
            <div className="">
              <FeatureUsageChart data={userSummary} loading={loading} />
            </div>
          </section>
        </div>
      ) : (
        <div className="graph-container">
          <div style={{ padding: "20px", width: "100%", height: "100%" }}>
            {!isSuperAdmin && (
              <AreaChartWithZoom
                Title={currentLanguage?.Analytics}
                selectedStore={selectedStore}
              />
            )}
          </div>
        </div>
      )}

      {!isSuperAdmin && (
        <>
          <div>
            <div className="d-flex justify-content-between mb-12 dropdownWithCustomDateContainer">
              <h2 className="text-20 font-600 text-141414">
                {currentLanguage?.metrics}
              </h2>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {selectedDaysLabel === "Custom Range" && (
                  <div className="custom-datepicker-wrapper">
                    <RangePicker
                      allowClear={false}
                      disabledDate={disabledDate}
                      placement="bottomLeft"
                      value={
                        customDateRange.startDate && customDateRange.endDate
                          ? [
                              dayjs(customDateRange.startDate),
                              dayjs(customDateRange.endDate),
                            ]
                          : []
                      }
                      onChange={(dates) => {
                        if (dates && dates.length === 2) {
                          const [start, end] = dates
                          setCustomDateRange({
                            startDate: start.toISOString(),
                            endDate: end.toISOString(),
                          })
                        }
                      }}
                    />
                  </div>
                )}
                <Dropdown
                  selectedCategory={selectedDaysLabel}
                  width="160px"
                  label={currentLanguage?.dropdown_1?.last_7_days}
                  iconSrc="/icons/selectstoreanalytics.svg"
                  options={[
                    {
                      label: currentLanguage?.dropdown_1?.last_7_days,
                      value: "Last 7 days",
                    },
                    {
                      label: currentLanguage?.dropdown_1?.last_30_days,
                      value: "Last 30 days",
                    },
                    {
                      label: currentLanguage?.dropdown_1?.last_90_days,
                      value: "Last 90 days",
                    },
                    {
                      label: currentLanguage?.chart?.custom_range,
                      value: "Custom Range",
                    },
                  ]}
                  onSelect={handleDropdownChange}
                />
              </div>
            </div>

            <div className="click-impression-container">
              <div className="row">
                <div className="col-4">
                  <ClickImpressionBox
                    loading={loading}
                    data={metricsData?.total_clicks}
                    title={currentLanguage?.total_clicks}
                    lang={lang}
                    currentLanguage={currentLanguage}
                  />
                  <div className="mb-16"></div>
                  <ClickImpressionBox
                    loading={loading}
                    data={metricsData?.total_views}
                    title={currentLanguage?.total_impressions}
                    lang={lang}
                    currentLanguage={currentLanguage}
                  />
                </div>

                <div className="col-8">
                  <div className="most-viewed">
                    <p className="text-14 text-2E2E30 font-500 mb-12">
                      {currentLanguage?.most_viewed}
                    </p>
                    <div className="overflow-auto" style={{ height: "225px" }}>
                      {loading ? (
                        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                          <Spin size="large" />
                        </div>
                      ) : metricsData?.top_10_products?.length > 0 ? (
                        metricsData?.top_10_products?.map((item) => (
                          <MostViewedItem
                            key={item.id}
                            item={item}
                            lang={lang}
                          />
                        ))
                      ) : !selectedStore ? (
                        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                          <p>
                            {currentLanguage?.select_store_to_view_analytics}
                          </p>
                        </div>
                      ) : (
                        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                          {currentLanguage?.no_data_right}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <StoreDrawer
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            stores={stores}
            selectedStore={selectedStore}
            onSelectStore={setSelectedStore}
            AllFilterOption={currentLanguage?.stores}
          />
        </>
      )}

      {isSuperAdmin && (
        <>
          <h5 style={{ paddingBottom: "10px" }}>
            {lang === "ar"
              ? "إحصاءات المستخدمين والإيرادات"
              : "User & Revenue Insights"}
          </h5>
          <UserRevenueInsights
            data={userRevenueData}
            loading={loading}
            filterState={filterState}
          />
        </>
      )}

      {isSuperAdmin && (
        <AdminLoginAnalytics filterState={filterState} />
      )}

      {isSuperAdmin && (
        <>
          {/* Same treatment as the "User & Revenue Insights" heading above.
              The section it follows already carries a 32px bottom margin, so the
              extra 20px of padding top and bottom left this heading marooned
              between the two. */}
          <h5 style={{ paddingBottom: "10px" }}>
            {lang === "ar" ? "إحصاءات حسب البلد" : "Insights by country"}
          </h5>
          <Insightsbycountry />
        </>
      )}

      {/* {isSuperAdmin && (
        <>
          <h5 style={{ paddingTop: "20px" }}>User Management</h5>
          <UserManagmentTable />
        </>
      )} */}
    </div>
  )
}

export default Dashboard
